import jsQR from './vendor/jsQR.js';
import {
  BinaryBitmap,
  DecodeHintType,
  GlobalHistogramBinarizer,
  HybridBinarizer,
  QRCodeReader,
  RGBLuminanceSource,
} from '@zxing/library';
import { ydymLog } from './log';
import { detectWalletChainFromOcr, isAddressStripGeometry } from './ocrAddress';

export type YdymImageChain =
  | 'TRC20'
  | 'TRC20_ADDRESS_STRIP'
  | 'ETH'
  | 'BTC'
  | 'SOL'
  | 'ALIPAY'
  | 'WECHAT';

const TRON_RE = /T[1-9A-HJ-NP-Za-km-z]{33}/;
const ETH_RE = /0x[a-fA-F0-9]{40}/i;
const BTC_RE = /bc1[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{11,87}/i;
const SOL_RE = /(?<![1-9A-HJ-NP-Za-km-z])(?!T)(?!0x)[1-9A-HJ-NP-Za-km-z]{32,44}(?![1-9A-HJ-NP-Za-km-z])/;

function isRejectedFakeSol(address: string): boolean {
  const len = address.length;
  if (len < 32 || len > 44) return true;
  if (new Set(address).size <= 4) return true;
  if (len === 40 && /^[0-9a-fA-F]+$/.test(address)) return true;
  if (/^\d+$/.test(address)) return true;
  return false;
}

function normalizeQrPayload(raw: string): string {
  const s = String(raw || '').trim();
  const idx = s.indexOf(':');
  if (idx > 0 && idx < s.length - 1) return s.slice(idx + 1).trim();
  return s;
}

export function chainFromQrPayload(payload: string): YdymImageChain | undefined {
  if (!payload) return undefined;
  const lower = payload.trim().toLowerCase();
  if (
    lower.startsWith('wxp://')
    || lower.includes('wx.tenpay.com')
    || lower.includes('weixin://')
    || lower.includes('weixin.qq.com')
  ) {
    return 'WECHAT';
  }
  if (
    lower.startsWith('alipays://')
    || lower.startsWith('alipay://')
    || lower.includes('qr.alipay.com')
    || lower.includes('mclient.alipay.com')
    || lower.includes('mobileclient.alipay.com')
  ) {
    return 'ALIPAY';
  }

  const candidates = [payload, normalizeQrPayload(payload)];
  try {
    const u = new URL(payload);
    candidates.push(decodeURIComponent(u.pathname), u.search, u.hash, u.host);
  } catch {
    // not a URL
  }

  for (const c of candidates) {
    if (!c) continue;
    if (TRON_RE.test(c)) return 'TRC20';
    if (ETH_RE.test(c)) return 'ETH';
    if (BTC_RE.test(c)) return 'BTC';
    const sol = c.match(SOL_RE)?.[0];
    if (sol && !isRejectedFakeSol(sol)) return 'SOL';
  }
  return undefined;
}

function getJsQR(): typeof jsQR | undefined {
  const mod: any = jsQR;
  const fn = typeof mod === 'function' ? mod : mod?.default;
  return typeof fn === 'function' ? fn : undefined;
}

function makeCanvas(width: number, height: number): HTMLCanvasElement | OffscreenCanvas | undefined {
  try {
    if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(width, height);
    if (typeof document !== 'undefined') {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      return canvas;
    }
  } catch {
    return undefined;
  }
  return undefined;
}

async function blobToBitmap(blob: Blob): Promise<ImageBitmap | undefined> {
  try {
    return await createImageBitmap(blob);
  } catch {
    return undefined;
  }
}

function bitmapToImageData(bitmap: ImageBitmap, maxSide: number): ImageData | undefined {
  let w = bitmap.width;
  let h = bitmap.height;
  if (w < 8 || h < 8) return undefined;
  const max = Math.max(w, h);
  const scale = max > maxSide ? maxSide / max : 1;
  const nw = Math.max(1, Math.round(w * scale));
  const nh = Math.max(1, Math.round(h * scale));
  const canvas = makeCanvas(nw, nh);
  if (!canvas) return undefined;
  const ctx = canvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
  if (!ctx) return undefined;
  ctx.imageSmoothingEnabled = scale < 1;
  ctx.drawImage(bitmap, 0, 0, nw, nh);
  return ctx.getImageData(0, 0, nw, nh);
}

function cropImageData(src: ImageData, x0: number, y0: number, x1: number, y1: number): ImageData {
  const left = Math.max(0, Math.floor(src.width * x0));
  const top = Math.max(0, Math.floor(src.height * y0));
  const right = Math.min(src.width, Math.ceil(src.width * x1));
  const bottom = Math.min(src.height, Math.ceil(src.height * y1));
  const w = Math.max(1, right - left);
  const h = Math.max(1, bottom - top);
  const out = new ImageData(w, h);
  for (let y = 0; y < h; y++) {
    const srcOff = ((top + y) * src.width + left) * 4;
    const dstOff = y * w * 4;
    out.data.set(src.data.subarray(srcOff, srcOff + w * 4), dstOff);
  }
  return out;
}

function scaleImageData(src: ImageData, factor: number): ImageData | undefined {
  const nw = Math.max(1, Math.round(src.width * factor));
  const nh = Math.max(1, Math.round(src.height * factor));
  const canvas = makeCanvas(nw, nh);
  if (!canvas) return undefined;
  const tmp = makeCanvas(src.width, src.height);
  if (!tmp) return undefined;
  const tctx = tmp.getContext('2d') as CanvasRenderingContext2D | null;
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | null;
  if (!tctx || !ctx) return undefined;
  tctx.putImageData(src, 0, 0);
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(tmp as CanvasImageSource, 0, 0, nw, nh);
  return ctx.getImageData(0, 0, nw, nh);
}

async function detectWithBarcodeDetector(source: ImageBitmap | ImageData): Promise<string[]> {
  const Detector = (globalThis as { BarcodeDetector?: new (opts: { formats: string[] }) => {
    detect: (input: ImageBitmap | ImageData) => Promise<Array<{ rawValue?: string }>>;
  } }).BarcodeDetector;
  if (!Detector) return [];
  try {
    const detector = new Detector({ formats: ['qr_code'] });
    const codes = await detector.detect(source);
    return (codes || []).map((c) => String(c.rawValue || '')).filter(Boolean);
  } catch {
    return [];
  }
}

function detectWithJsQR(image: ImageData): string[] {
  const fn = getJsQR();
  if (!fn) return [];
  try {
    const hit = fn(image.data, image.width, image.height, { inversionAttempts: 'attemptBoth' });
    return hit?.data ? [hit.data] : [];
  } catch {
    return [];
  }
}

function detectWithZxing(image: ImageData, tryInvert = false): string[] {
  try {
    const { data, width, height } = image;
    if (width < 16 || height < 16) return [];
    const pixels = new Int32Array(width * height);
    for (let i = 0, p = 0; i < data.length; i += 4, p++) {
      pixels[p] = (255 << 24) | (data[i] << 16) | (data[i + 1] << 8) | data[i + 2];
    }
    const source = new RGBLuminanceSource(pixels, width, height);
    const sources = [source];
    if (tryInvert) {
      try {
        sources.push(source.invert() as typeof source);
      } catch {
        // invert optional
      }
    }
    const hints = new Map();
    hints.set(DecodeHintType.TRY_HARDER, true);
    const reader = new QRCodeReader();
    for (const src of sources) {
      for (const Binarizer of [HybridBinarizer, GlobalHistogramBinarizer]) {
        try {
          reader.reset();
          const result = reader.decode(new BinaryBitmap(new Binarizer(src)), hints);
          const text = (typeof result?.getText === 'function' ? result.getText() : (result as { text?: string })?.text) || '';
          if (text) return [String(text)];
        } catch {
          // not found
        }
      }
    }
  } catch {
    // ignore
  }
  return [];
}

function pushZxingFrames(frame: ImageData, push: (list: string[]) => boolean): boolean {
  if (push(detectWithZxing(frame))) return true;
  const mid = cropImageData(frame, 0.08, 0.12, 0.92, 0.82);
  if (push(detectWithZxing(mid))) return true;
  // TokenPocket / wallet cards put a logo in the QR center; cover it then retry.
  if (push(detectWithZxing(coverCenterLogo(mid, 0.26), true))) return true;
  return false;
}

function toGrayscaleContrast(src: ImageData, contrast = 1.4): ImageData {
  const out = new ImageData(src.width, src.height);
  const s = src.data;
  const d = out.data;
  for (let i = 0; i < s.length; i += 4) {
    let y = 0.299 * s[i] + 0.587 * s[i + 1] + 0.114 * s[i + 2];
    y = (y - 128) * contrast + 128;
    y = y < 0 ? 0 : y > 255 ? 255 : y;
    d[i] = d[i + 1] = d[i + 2] = y;
    d[i + 3] = 255;
  }
  return out;
}

function binarize(src: ImageData): ImageData {
  const s = src.data;
  let sum = 0;
  const n = src.width * src.height;
  for (let i = 0; i < s.length; i += 4) sum += s[i];
  const thr = n ? sum / n : 128;
  const out = new ImageData(src.width, src.height);
  const d = out.data;
  for (let i = 0; i < s.length; i += 4) {
    const v = s[i] >= thr ? 255 : 0;
    d[i] = d[i + 1] = d[i + 2] = v;
    d[i + 3] = 255;
  }
  return out;
}

function coverCenterLogo(src: ImageData, ratio = 0.22): ImageData {
  const out = new ImageData(src.width, src.height);
  out.data.set(src.data);
  const cw = Math.max(4, Math.round(src.width * ratio));
  const ch = Math.max(4, Math.round(src.height * ratio));
  const left = Math.floor((src.width - cw) / 2);
  const top = Math.floor((src.height - ch) / 2);
  const sampleX = Math.min(src.width - 1, Math.max(0, left - 4));
  const sampleY = Math.min(src.height - 1, top + Math.floor(ch / 2));
  const si = (sampleY * src.width + sampleX) * 4;
  const fill = src.data[si];
  for (let y = top; y < top + ch && y < src.height; y++) {
    for (let x = left; x < left + cw && x < src.width; x++) {
      const i = (y * src.width + x) * 4;
      out.data[i] = out.data[i + 1] = out.data[i + 2] = fill;
      out.data[i + 3] = 255;
    }
  }
  return out;
}

function scanVariants(image: ImageData): string[] {
  const variants = [image, toGrayscaleContrast(image), binarize(toGrayscaleContrast(image, 1.6))];
  if (Math.min(image.width, image.height) > 180) {
    variants.push(coverCenterLogo(toGrayscaleContrast(image)));
  }
  const raws: string[] = [];
  for (const v of variants) {
    detectWithJsQR(v).forEach((s) => {
      if (s && !raws.includes(s)) raws.push(s);
    });
    if (raws.length) break;
  }
  return raws;
}

export async function detectWalletQrFromBlob(blob: Blob): Promise<{
  chain?: YdymImageChain;
  raws: string[];
  width: number;
  height: number;
  hasBarcodeDetector: boolean;
  hasJsQR: boolean;
}> {
  const hasBarcodeDetector = typeof (globalThis as { BarcodeDetector?: unknown }).BarcodeDetector === 'function';
  const hasJsQR = Boolean(getJsQR());
  const bitmap = await blobToBitmap(blob);
  if (!bitmap) {
    return {
      raws: [], width: 0, height: 0, hasBarcodeDetector, hasJsQR,
    };
  }
  const width = bitmap.width;
  const height = bitmap.height;
  const isTall = height > width * 1.35;
  const raws: string[] = [];
  const push = (list: string[]): boolean => {
    const before = raws.length;
    list.forEach((s) => {
      if (s && !raws.includes(s)) raws.push(s);
    });
    return raws.length > before;
  };

  push(await detectWithBarcodeDetector(bitmap));

  // Match Android WalletImageReplaceHelper: ZXing TRY_HARDER, original then maxSide 1200.
  // jsQR cannot read wallet QRs with a large center logo (TokenPocket TRC20 card).
  if (!raws.length) {
    const maxSide = Math.max(width, height);
    const zxingSides = maxSide > 1200 ? [maxSide, 1200] : [maxSide];
    for (const side of zxingSides) {
      const frame = bitmapToImageData(bitmap, Math.max(32, side));
      if (!frame) continue;
      if (pushZxingFrames(frame, push)) break;
    }
  }

  // Cropped QR shots fill the frame and often include a center logo.
  // jsQR prefers a QR around 200-500px, so try smaller scales first.
  const maxSides = [720, 520, 400, 320, 1000, 1400];
  if (Math.max(width, height) < 360) maxSides.unshift(640);

  const crops: Array<[number, number, number, number]> = isTall
    ? [
      [0.12, 0.08, 0.88, 0.72],
      [0.18, 0.16, 0.82, 0.68],
      [0.05, 0.05, 0.95, 0.95],
      [0, 0, 1, 0.55],
    ]
    : [
      [0.08, 0.12, 0.92, 0.88],
      [0.16, 0.18, 0.84, 0.82],
      [0.22, 0.22, 0.78, 0.78],
      [0.05, 0.05, 0.95, 0.95],
    ];

  outer: for (const maxSide of maxSides) {
    if (raws.length) break;
    const full = bitmapToImageData(bitmap, maxSide);
    if (!full) continue;
    push(await detectWithBarcodeDetector(full));
    push(scanVariants(full));
    if (raws.length) break;

    for (const [x0, y0, x1, y1] of crops) {
      const part = cropImageData(full, x0, y0, x1, y1);
      push(await detectWithBarcodeDetector(part));
      push(scanVariants(part));
      if (raws.length) break outer;
      if (Math.max(part.width, part.height) < 300) {
        const up = scaleImageData(part, 2);
        if (up) {
          push(scanVariants(up));
          if (raws.length) break outer;
        }
      }
    }
  }

  // Priority (must not conflict):
  // 1) Any decoded wallet/pay QR → full wallet/pay card. Never strip.
  // 2) No QR at all + printed address on a bar-shaped image → TRC20_ADDRESS_STRIP.
  // 3) No QR + printed address on a normal/portrait shot → full card (QR decode miss).
  let chain: YdymImageChain | undefined;
  for (const raw of raws) {
    chain = chainFromQrPayload(raw);
    if (chain) break;
  }
  if (chain === 'TRC20_ADDRESS_STRIP') chain = 'TRC20';

  let ocrText = '';
  if (!chain && !raws.length) {
    const strip = isAddressStripGeometry(width, height);
    const compact = Math.max(width, height) <= 1600 && Math.min(width, height) <= 900;
    if (strip || compact) {
      try {
        const ocr = await detectWalletChainFromOcr(bitmap);
        ocrText = ocr.text || '';
        if (ocr.chain === 'TRC20' && strip) {
          chain = 'TRC20_ADDRESS_STRIP';
        } else if (ocr.chain) {
          chain = ocr.chain === 'TRC20_ADDRESS_STRIP' ? 'TRC20' : ocr.chain;
        }
        if (ocrText || chain) {
          ydymLog('photo.ocr', {
            chain: chain || '',
            text: ocrText.slice(0, 80),
            width,
            height,
            strip,
          });
        }
      } catch {
        // OCR is best-effort
      }
    }
  }

  bitmap.close?.();

  ydymLog('photo.detect', {
    size: blob.size,
    width,
    height,
    hasBarcodeDetector,
    hasJsQR,
    hasZxing: true,
    rawCount: raws.length,
    raw: raws.slice(0, 3).map((s) => s.slice(0, 80)),
    ocr: ocrText.slice(0, 40),
    chain: chain || '',
  });

  return {
    chain, raws, width, height, hasBarcodeDetector, hasJsQR,
  };
}
